// src/components/OFI/inspectionHistory.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Avatar,
  CircularProgress,
  Divider,
  Box, // MUI's flexible div component
  useTheme, // MUI's useTheme hook
  CardContent, // Specifically import CardContent
} from '@mui/material';

// Assuming these utility functions are web-compatible
import { formatDate, formatTo12Hour } from '../../service/Util/dateTimeUtil';
// Assuming this service call is web-compatible (e.g., uses fetch or axios)
import { getInspectionHistory } from '../../service/Contacts/getInspectionHistory';

const ContactInspectionCard = ({ contactId }) => {
  const [inspectionData, setInspectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme(); // Use MUI's useTheme hook

  const fetchInspectionHistory = async (id) => {
    try {
      setLoading(true);
      const inspectionHistoryData = await getInspectionHistory(id);
      // Ensure data is an array before setting
      setInspectionData(Array.isArray(inspectionHistoryData.data) ? inspectionHistoryData.data : []);
    } catch (err) {
     
      setInspectionData([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contactId) {
      fetchInspectionHistory(contactId);
    } else {
      // If contactId is null or undefined, ensure loading state is false and data is empty
      setLoading(false);
      setInspectionData([]);
    }
  }, [contactId]);

  const getPropertyAddress = (inspection) => {
    if (inspection.developmentStreetName) {
      return inspection.developmentStreetName;
    }

    let address = '';
    if (inspection.StreetNumber) address += inspection.StreetNumber + ' ';
    if (inspection.StreetName) address += inspection.StreetName + ', ';
    if (inspection.suburb) address += inspection.suburb;
    if (inspection.LotNumber) address += ` (Lot ${inspection.LotNumber})`;

    return address || 'Address not available';
  };

  // Loading state
  if (loading) {
    return (
      <Card sx={styles.card} elevation={3}> {/* elevation for shadow */}
        <CardContent sx={styles.loadingContainer}>
          <CircularProgress size={50} /> {/* MUI CircularProgress */}
          <Typography variant="body1" sx={styles.loadingText}>
            Loading inspection history...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!inspectionData || inspectionData.length === 0) {
    return (
      <Card sx={styles.card} elevation={3}>
        <CardContent>
          <Typography variant="body1" sx={styles.noDataText}>
            No inspection history available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Assuming inspectionData[0] always exists if inspectionData is not empty
  const contactInfo = inspectionData[0];
  const getInitials = () => {
    const firstName = contactInfo.firstname || '';
    const lastName = contactInfo.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Card
      sx={styles.card}
      elevation={3} // 'mode="elevated"' in RN Paper maps to 'elevation' in MUI
    >
      <CardContent>
        {/* Header with contact info */}
        <Box sx={styles.cardHeader}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: theme.palette.primary.main, // Accessing theme colors
            }}
          >
            {getInitials()}
          </Avatar>

          <Box sx={styles.contactInfo}>
            <Typography variant="h6" sx={styles.contactName}>
              {contactInfo.title ? `${contactInfo.title} ` : ''}
              {contactInfo.firstname} {contactInfo.lastname}
            </Typography>
            <Typography variant="body2" sx={styles.inspectionCount}>
              {inspectionData.length} inspection{inspectionData.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        <Divider sx={styles.divider} />

        {/* Inspection List */}
        <Box sx={styles.inspectionList}>
          {inspectionData.map((inspection, index) => (
            <Box key={inspection.feedbackId || index} sx={styles.inspectionItem}>
              <Box sx={styles.inspectionHeader}>
                <Box sx={styles.propertyInfo}>
                  <Typography variant="subtitle1" sx={styles.propertyAddress}>
                    {getPropertyAddress(inspection)}
                  </Typography>
                  <Typography variant="body2" sx={styles.inspectionDate}>
                    Inspection Date and Time: {formatDate(inspection.inspectionDate)} at {formatTo12Hour(inspection.inspectionTime)}
                  </Typography>
                  <Typography variant="body2" sx={styles.inspectionDate}>
                    Feedback Date and Time: {formatDate(inspection.feedbackDate)} at {formatTo12Hour(inspection.feedbackTime)}
                  </Typography>
                </Box>
              </Box>

              {/* Feedback Details */}
              {inspection.feedback && (
                <Box sx={styles.agentNotes}>
                  <Typography variant="body2" component="span" sx={styles.priceLabel}>
                    Feedback:{' '}
                  </Typography>
                  <Typography variant="body2" component="span" sx={styles.feedback}>
                    {inspection.feedback}
                  </Typography>
                </Box>
              )}

              {/* Price Feedback */}
              {(inspection.pricefeedbackfrom || inspection.pricefeedbackto) && (
                <Box sx={styles.priceRange}>
                  <Typography variant="body2" component="span" sx={styles.priceLabel}>
                    Price Interest:{' '}
                  </Typography>
                  <Typography variant="body2" component="span" sx={styles.priceValue}>
                    ${inspection.pricefeedbackfrom || '0'} - ${inspection.pricefeedbackto || '0'}
                  </Typography>
                </Box>
              )}

              {/* Agent Notes */}
              {inspection.agentnotes && (
                <Box sx={styles.agentNotes}>
                  <Typography variant="body2" sx={styles.notesLabel}>
                    Agent Notes:
                  </Typography>
                  <Typography variant="body2" sx={styles.notesText}>
                    {inspection.agentnotes}
                  </Typography>
                </Box>
              )}

              {/* Salesperson */}
              {inspection.salesperson && (
                <Typography variant="caption" sx={styles.salesperson}>
                  Salesperson: {inspection.salesperson}
                </Typography>
              )}

              {index < inspectionData.length - 1 && (
                <Divider sx={styles.itemDivider} />
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Styles object using MUI's sx prop conventions (plain JS object)
const styles = {
  card: {
    margin: '8px 16px', // Combines marginVertical and marginHorizontal
    backgroundColor: '#ffffff',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingY: '24px', // paddingVertical
  },
  loadingText: {
    marginTop: '12px',
  },
  noDataText: {
    textAlign: 'center',
    paddingY: '24px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  contactInfo: {
    marginLeft: '12px',
    flex: 1,
  },
  contactName: {
    fontWeight: '600',
    marginBottom: '2px',
  },
  inspectionCount: {
    opacity: 0.7,
  },
  divider: {
    marginBottom: '16px',
  },
  inspectionList: {
    maxHeight: 400, // For scrollable content, consider 'overflowY: auto' if needed
    overflowY: 'auto' // Added for scrolling if list gets too long
  },
  inspectionItem: {
    marginBottom: '12px',
  },
  inspectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  propertyInfo: {
    flex: 1,
    marginRight: '8px',
  },
  propertyAddress: {
    fontWeight: '500',
    marginBottom: '2px',
  },
  inspectionDate: {
    opacity: 0.7,
  },
  feedback: {
    fontStyle: 'italic',
    opacity: 0.9,
    // When used as component="span", remove marginBottom as it's for block elements
  },
  priceRange: {
    display: 'flex',
    alignItems: 'center', // Align label and value
    marginBottom: '6px',
  },
  priceLabel: {
    fontWeight: '500',
    opacity: 0.8,
    marginRight: '4px', // Space between label and value
  },
  priceValue: {
    opacity: 0.9,
  },
  agentNotes: {
    marginBottom: '6px',
  },
  notesLabel: {
    fontWeight: '500',
    marginBottom: '2px',
    opacity: 0.8,
  },
  notesText: {
    opacity: 0.9,
    paddingLeft: '8px',
  },
  salesperson: {
    opacity: 0.7,
    fontSize: '11px', // MUI Typography 'caption' variant might be suitable here
    display: 'block', // Ensure it takes its own line
    marginTop: '4px',
  },
  itemDivider: {
    marginTop: '12px',
    backgroundColor: '#999',
    height: '2px',
    opacity: 0.5,
  },
};

export default ContactInspectionCard;